import { useNavigate } from 'react-router';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, Center, Column, Divider, Row, ZStack } from '~/commonComponents';
import { EnablePermanentJobM2M } from '~/constants/ff.posthog';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import HY_Tooth from '~/images/HY_tooth.png';
import { ChevronRight } from '~/images/icons';
import HeartBorderedIcon from '~/images/icons/HeartBorderedIcon';
import ShieldCheckIcon from '~/images/shield-check.svg';
import StarIcon from '~/images/star_icon.svg';
import { IJob, IUser } from '~/models';
import { TNullable } from '~/types/Nullable';
import { CompanyMatchLabel } from '../CompanyMatchLabel/CompanyMatchLabel';

interface IProfessionalProfileProps {
  job?: TNullable<IJob>;
  professional: TNullable<IUser>;
  disableNavigation?: boolean;
  hasBackgroundCheck?: boolean;
  hasWorkedWithDSO?: boolean;
  hasWorkedWithOffice?: boolean;
  displayProfession?: string;
  onTrackOverviewClick?: () => void;
}

export const ProfessionalProfile = ({
  job,
  professional,
  disableNavigation = false,
  hasBackgroundCheck = false,
  hasWorkedWithDSO = false,
  hasWorkedWithOffice = false,
  displayProfession,
  onTrackOverviewClick,
}: IProfessionalProfileProps) => {
  const navigate = useNavigate();

  const isEnablePermanentJobM2M = useFeatureFlag(EnablePermanentJobM2M);

  const isFavorite = professional?.isFavorite;
  const hasProfileImage = !!professional?.profile_image;

  const handleNavigation = () => {
    if (!job || !professional) return;

    onTrackOverviewClick?.();

    if (isEnablePermanentJobM2M && job.isOnsiteInterview) {
      navigate(`/permanent-jobs/${job.postingId}/candidates/${professional.id}/overview`);
    } else {
      navigate(`/dashboard/candidate/${job.id}`, {
        state: {
          candidateId: professional?.id,
          state: job?.state,
          profession: job?.profession,
          subProfession: job?.subProfession,
        },
      });
    }
  };

  return (
    <Row alignItems="center" gap={20}>
      <Center
        borderColor={isFavorite ? colors.favorites[400] : 'transparent'}
        borderRadius="100%"
        borderStyle="solid"
        borderWidth={3}
        height={104}
        padding={2}
        position="relative"
        width={104}
      >
        {hasProfileImage ? (
          <img
            src={professional.profile_image}
            style={{
              borderRadius: 104,
              height: 104,
              objectFit: 'cover',
              width: 104,
            }}
            alt="profile_image"
          />
        ) : (
          <Box className="default_circle_container">
            <img src={HY_Tooth} alt="profile_image" width="104px" />
          </Box>
        )}
        {isFavorite && (
          <ZStack right={-4} top="72%">
            <HeartBorderedIcon />
          </ZStack>
        )}
      </Center>

      <Column marginBottom={20}>
        <Row alignItems="center" flex={1} gap={8} marginTop={15}>
          <Typography
            as="span"
            bold
            size="heading_4"
            numberOfLines={1}
            style={{ maxWidth: 280 }}
          >
            {professional?.full_name}
          </Typography>

          <Row alignItems="center" gap={4}>
            <img
              src={StarIcon}
              alt="star_icon"
              style={{
                height: 24,
                width: 24,
              }}
            />
            <Typography as="span" bold size="heading_4">
              {professional?.average_rating?.toFixed(1) || '5.0'}
            </Typography>
          </Row>

          {!disableNavigation && (
            <>
              <Divider color={colors.neutral[600]} height={24} marginVertical={0} width={1.5} />
              <Row alignItems="center" cursor="pointer" gap={8} onClick={handleNavigation}>
                <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
                  {isEnablePermanentJobM2M && job?.isOnsiteInterview ? 'Overview' : 'Profile'}
                </Typography>
                <ChevronRight size={24} />
              </Row>
            </>
          )}
        </Row>
        <Typography as="span">Drivers License Verified</Typography>
        <Row alignItems="center" gap={4} marginBottom={8}>
          <Typography as="span">{displayProfession} Credentials Verified</Typography>
          {hasBackgroundCheck && (
            <>
              <Divider color={colors.neutral[600]} height={16} marginVertical={0} width={1.5} />
              <Row alignItems="center" gap={4}>
                <Typography as="span">Background Checked</Typography>
                <img
                  src={ShieldCheckIcon}
                  alt="shield-check"
                  style={{
                    height: 24,
                    margin: 0,
                    width: 24,
                  }}
                />
              </Row>
            </>
          )}
        </Row>
        <CompanyMatchLabel
          offerOwnerId={job?.offer_owner.id || ''}
          professional={professional}
          hasWorkedWithDSO={hasWorkedWithDSO}
          hasWorkedWithOffice={hasWorkedWithOffice}
        />
      </Column>
    </Row>
  );
};
